/**
 * ZendeskWidget Component
 *
 * Renders a toggle button that opens/closes an iframe containing the Zendesk support widget.
 * The widget is loaded from a separate, isolated application (app-zendesk-widget) running in a dedicated Nomad job.
 *
 * @remarks
 * Security Integration:
 * - The Zendesk widget executes third-party code hosted by Zendesk
 * - To mitigate security risks, the widget runs in an isolated iframe from a separate application
 * - The iframe origin is validated against a whitelisted origin from Redux state
 * - Parent-child communication uses the postMessage API with origin verification
 * - The widget can only close itself by sending a "zendesk-widget-closed" message
 *
 * @returns A button that toggles the visibility of the Zendesk widget iframe
 */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { zendeskWidgetOrigin as selectZendeskWidgetOrigin } from "../../state/config/config.selector";
import { useI18n } from "../../utils/hooks/use.i18n";
import styles from "./ZendeskWidget.module.css";

export const ZendeskWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const widgetOrigin = useSelector(selectZendeskWidgetOrigin);
  const t = useI18n("ZENDESK_WIDGET.");

  useEffect(() => {
    if (!isOpen || !widgetOrigin) return;
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== widgetOrigin) return;
      if (event.data?.type === "zendesk-widget-closed") {
        setIsOpen(false);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isOpen, widgetOrigin]);

  return (
    <>
      {isOpen && widgetOrigin && (
        <iframe
          className={styles.frame}
          src={`${widgetOrigin}/`}
          title="Zendesk support widget"
        />
      )}
      {/* The button is always rendered, but the iframe is only rendered when the button is clicked (isOpen is true) */}
      <button
        aria-label={t("ARIA_LABEL")}
        className={styles.button}
        data-test-id="zendesk-button"
        onClick={() => setIsOpen((prev) => !prev)}
        title={t("TITLE")}
        type="button"
      >
        <svg
          aria-hidden="true"
          className={styles.icon}
          fill="none"
          height="100%"
          viewBox="-10 -15 120 120"
          width="100%"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            className={styles.path}
            d="M75.9,21H24.1c-4.4,0-8.1,4-8.1,8.4v28.4c0,4.4,3.6,8,8,8l24.9-.4,17.5,13.3-.8-12.7h10.3c4.4,0,8.1-3.8,8.1-8.2v-28.4c0-4.4-3.7-8.4-8.1-8.4ZM46.09,54.09v-2.8c0-.5.41-.87.86-.87h2.8c.5,0,.87.42.87.87v2.8c0,.49-.41.86-.87.86h-2.8c-.45,0-.86-.41-.86-.86ZM53.58,43.18l-2.1.99c-.7.37-1.32.82-1.32,2.01v1.73c0,.5-.08.87-.57.87h-2.6c-.49,0-.94-.37-.94-.87v-1.73c0-2.51,1.07-4.11,3.46-5.35l1.85-.95c.58-.28.86-.82.86-1.44v-.45c0-.74-.24-1.61-2.63-1.61-1.32,0-4.37.46-4.94.54h-.13c-.45,0-.82-.37-.86-.82l-.17-2.06c-.04-.46.29-.87.7-.95,1.36-.25,3.46-.58,5.48-.58,4.36,0,6.67,2.02,7.08,5.36v.53h.04c0,1.98-1.15,3.67-3.21,4.78Z"
          />
        </svg>
      </button>
    </>
  );
};
